import { TIPO_LABEL } from './slug';
import {
  ImoveisFilters,
  STATUS_LABELS,
  CATEGORIA_LABELS,
  TIPO_LABELS,
  REFERENCIA_LABELS,
} from './imoveis-filters';

/**
 * Geração de title/description dinâmicos e alinhados aos anúncios de Google Ads
 * (Quality Score) para as landing pages de listagem.
 *
 * Multi-tenant: `brandName` (= tenant.nome), `cidade` e `bairroNome` (nome real
 * resolvido do banco) são INJETADOS pela page — não há marca hardcoded aqui.
 */
export interface ListingMetadataParams {
  finalidade?: string; // slug: 'venda' | 'aluguel' | undefined (page raiz /imoveis)
  tipoRota?: string; // slug da rota: 'apartamentos' | undefined (page raiz)
  bairroNome?: string | null; // nome real do bairro resolvido no banco
  cidade: string; // cidade do tenant (ex.: 'Goiânia')
  filters: ImoveisFilters;
  brandName: string; // tenant.nome — assinatura da description (o sufixo do title
  // vem do template do layout do tenant: `%s | ${tenant.nome}`)
}

/** "à venda"/"para alugar" (minúsculo, title/description). Vazio sem finalidade. */
function acaoLower(finalidade?: string): string {
  return finalidade === 'venda' ? 'à venda' : finalidade === 'aluguel' ? 'para alugar' : '';
}

/** "à Venda" (H1). Vazio quando não há segmento de finalidade (page raiz). */
function acaoTitle(finalidade?: string): string {
  return finalidade === 'venda' ? 'à Venda' : finalidade === 'aluguel' ? 'para alugar' : '';
}

/** Remove espaços duplos resultantes de `acao` vazio. */
function collapseSpaces(text: string): string {
  return text.replace(/\s{2,}/g, ' ').trim();
}

/**
 * Normaliza nomes em CAIXA ALTA do banco (ex.: "JARDIM GOIÁS") para Title Case
 * ("Jardim Goiás"), preservando acentos. Usado em labels exibidos (title/h1).
 */
export function toTitleCasePtBr(text: string): string {
  return text
    .trim()
    .split(/\s+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

const PREP_NO = new Set([
  'setor', 'jardim', 'parque', 'residencial', 'bairro', 'conjunto',
  'loteamento', 'condomínio', 'condominio', 'sítio', 'sitio',
]);
const PREP_NA = new Set(['vila', 'quadra', 'chácara', 'chacara', 'fazenda', 'cidade']);

/**
 * Preposição correta para um nome de bairro pt-BR, pela primeira palavra:
 * "Setor Bueno" → "no", "Vila Brasília" → "na", desconhecido → "em" (neutro).
 */
export function bairroPreposition(nome: string): 'no' | 'na' | 'em' {
  const first = nome.trim().split(/\s+/)[0]?.toLowerCase() ?? '';
  if (PREP_NO.has(first)) return 'no';
  if (PREP_NA.has(first)) return 'na';
  return 'em';
}

function buildLocation(
  bairroNome: string | null | undefined,
  cidade: string
): { prep: string; label: string } {
  if (bairroNome) {
    return { prep: bairroPreposition(bairroNome), label: toTitleCasePtBr(bairroNome) };
  }
  return { prep: 'em', label: cidade };
}

/** Label do tipo: subtipo (filtro) > tipo da rota > 'Imóveis' (page raiz). */
function resolveTipoLabel(tipoRota: string | undefined, filters: ImoveisFilters): string {
  if (filters.tipo) {
    return TIPO_LABELS[filters.tipo] ?? filters.tipo;
  }
  return tipoRota ? (TIPO_LABEL[tipoRota] ?? tipoRota) : 'Imóveis';
}

export function buildListingTitle(params: ListingMetadataParams): string {
  const { finalidade, tipoRota, bairroNome, cidade, filters } = params;
  const { prep, label } = buildLocation(bairroNome, cidade);
  const acao = acaoLower(finalidade);
  const tipoLabel = resolveTipoLabel(tipoRota, filters);

  const text = (() => {
    if (filters.referencia) {
      const refLabel = REFERENCIA_LABELS[filters.referencia] ?? filters.referencia;
      return `${tipoLabel} próximos ao ${refLabel} ${prep} ${label}`;
    }

    if (filters.status) {
      const statusLabel = STATUS_LABELS[filters.status] ?? filters.status;
      return `${tipoLabel} ${statusLabel} ${prep} ${label}`;
    }

    if (filters.categoria) {
      const catLabel = CATEGORIA_LABELS[filters.categoria] ?? filters.categoria;
      return `${tipoLabel} ${catLabel} ${prep} ${label}`;
    }

    const parts: string[] = [];
    if (filters.suites) parts.push(`${filters.suites} suítes`);
    if (filters.quartos) parts.push(`${filters.quartos} quartos`);
    if (filters.vagas) parts.push(`${filters.vagas} vagas`);

    if (parts.length > 0) {
      return `${tipoLabel} com ${parts.join(' e ')} ${acao} ${prep} ${label}`;
    }

    return `${tipoLabel} ${acao} ${prep} ${label}`;
  })();

  return collapseSpaces(text);
}

/**
 * Texto base do H1 no padrão Zap Imóveis, SEM o count: "{Tipo} {complemento}
 * em {Bairro}, {Cidade}". O número é prefixado pelo componente (count reativo
 * via useImoveis.total), então não entra aqui.
 * Não leva sufixo de marca (isso é exclusivo do <title>).
 */
export function buildListingH1(params: {
  finalidade?: string;
  tipoRota?: string;
  bairro?: string | null;
  cidade?: string;
  filters: ImoveisFilters;
}): string {
  const { finalidade, tipoRota, bairro, cidade = 'Goiânia', filters } = params;
  const acao = acaoTitle(finalidade);
  const tipoLabel = resolveTipoLabel(tipoRota, filters);
  const local = bairro
    ? `em ${toTitleCasePtBr(bairro)}, ${cidade}`
    : `em ${cidade}`;

  const text = (() => {
    if (filters.referencia) {
      const refLabel = REFERENCIA_LABELS[filters.referencia] ?? filters.referencia;
      return `${tipoLabel} próximos ao ${refLabel} ${local}`;
    }
    if (filters.status) {
      const statusLabel = STATUS_LABELS[filters.status] ?? filters.status;
      return `${tipoLabel} ${statusLabel} ${local}`;
    }
    if (filters.categoria) {
      const catLabel = CATEGORIA_LABELS[filters.categoria] ?? filters.categoria;
      return `${tipoLabel} ${catLabel} ${acao} ${local}`;
    }
    if (filters.quartos) {
      return `${tipoLabel} com ${filters.quartos} quartos ${acao} ${local}`;
    }
    if (filters.suites) {
      return `${tipoLabel} com ${filters.suites} suítes ${acao} ${local}`;
    }

    return `${tipoLabel} ${acao} ${local}`;
  })();

  return collapseSpaces(text);
}

export function buildListingDescription(params: ListingMetadataParams): string {
  const { finalidade, tipoRota, bairroNome, cidade, filters, brandName } = params;
  const { prep, label } = buildLocation(bairroNome, cidade);
  const acao = acaoLower(finalidade);
  const tipoLabel = resolveTipoLabel(tipoRota, filters);

  const text = (() => {
    if (filters.referencia) {
      const refLabel = REFERENCIA_LABELS[filters.referencia] ?? filters.referencia;
      return `Veja ${tipoLabel.toLowerCase()} ${acao} próximos ao ${refLabel} ${prep} ${label}. Consultoria com ${brandName}.`;
    }

    if (filters.status === 'pronto-para-morar') {
      return `${tipoLabel} prontos para morar ${prep} ${label}. Entrega imediata, sem espera. Fale com ${brandName}.`;
    }

    if (filters.categoria === 'alto-padrao') {
      return `${tipoLabel} de alto padrão ${prep} ${label}. Acabamentos premium e localização privilegiada. ${brandName}.`;
    }

    if (filters.tipo === 'cobertura') {
      return `Coberturas ${acao} ${prep} ${label}. Amplos terraços e vista privilegiada. Consultoria de ${brandName}.`;
    }

    const parts: string[] = [];
    if (filters.suites) parts.push(`${filters.suites} suítes`);
    if (filters.quartos) parts.push(`${filters.quartos} quartos`);
    if (filters.vagas) parts.push(`${filters.vagas} vagas`);

    if (parts.length > 0) {
      return `${tipoLabel} com ${parts.join(', ')} ${acao} ${prep} ${label}. Atendimento especializado com ${brandName}.`;
    }

    return `${tipoLabel} ${acao} ${prep} ${label}. Consultoria com ${brandName}, especialista na região.`;
  })();

  return collapseSpaces(text);
}
